import { CommonModule } from '@angular/common';
import { Component, DestroyRef, effect, HostListener, OnDestroy, OnInit, computed, inject, signal } from '@angular/core';
import { Router, RouterLink, RouterLinkActive, RouterOutlet, NavigationEnd } from '@angular/router';
import { toSignal, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { map, filter } from 'rxjs/operators';
import { IonicModule } from '@ionic/angular';
import { AuthService, ThemeService, VendorContextService, DialogService, LoggerService, isGroceryVendor, AppFooterComponent } from '@shared';
import { VendorSessionTeardownService } from '../../services/vendor-session-teardown.service';
import { NewOrderAlertService } from '../../services/new-order-alert.service';
import { PendingOrderReminderService } from '../../services/pending-order-reminder.service';
import { VendorOrdersService } from '../../core/services/vendor-orders.service';
import { VendorSideMenuService } from '../../services/vendor-side-menu.service';
import { VendorEarningsGateService } from '../../services/vendor-earnings-gate.service';
import { ExpandableOrderSearchComponent } from '../../components/expandable-order-search/expandable-order-search.component';

interface NavItem {
  label: string;
  route: string;
  icon: string;
  badge?: string;
}

interface NavSection {
  title: string;
  items: NavItem[];
}

const NAV_SECTIONS: NavSection[] = [
  {
    title: 'Main',
    items: [
      { label: 'Live Orders', route: '/tabs/orders', icon: 'receipt-outline', badge: 'Live' },
      { label: 'Products', route: '/tabs/products', icon: 'fast-food-outline' },
      { label: 'Menu categories', route: '/tabs/custom-menu-categories', icon: 'albums-outline' },
      { label: 'Inventory', route: '/tabs/inventory', icon: 'cube-outline' },
      { label: 'Orders History', route: '/tabs/orders-history', icon: 'time-outline' },
    ],
  },
  {
    title: 'Business',
    items: [
      { label: 'Earnings', route: '/tabs/earnings', icon: 'wallet-outline' },
      { label: 'Ratings & Reviews', route: '/tabs/ratings', icon: 'star-outline' },
      { label: 'Store Settings', route: '/tabs/store', icon: 'storefront-outline' },
      { label: 'POS staff', route: '/tabs/pos-users', icon: 'people-outline' },
      // { label: 'Campaigns', route: '/tabs/campaigns', icon: 'megaphone-outline' },
    ],
  },
  {
    title: 'Operations',
    items: [
      { label: 'Delivery', route: '/tabs/delivery', icon: 'bicycle-outline' },
      { label: 'Notifications', route: '/tabs/notifications', icon: 'notifications-outline' },
      { label: 'Settings', route: '/tabs/settings', icon: 'settings-outline' },
    ],
  },
];

@Component({
  selector: 'app-vendor-shell',
  standalone: true,
  imports: [CommonModule, RouterLink, RouterLinkActive, RouterOutlet, IonicModule, AppFooterComponent, ExpandableOrderSearchComponent],
  templateUrl: './vendor-shell.component.html',
  styleUrls: ['./vendor-shell.component.scss'],
})
export class VendorShellComponent implements OnInit, OnDestroy {
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);
  private readonly themeService = inject(ThemeService);
  private readonly vendorContext = inject(VendorContextService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly dialogService = inject(DialogService);
  private readonly logger = inject(LoggerService);
  private readonly sessionTeardown = inject(VendorSessionTeardownService);
  private readonly newOrderAlertService = inject(NewOrderAlertService);
  private readonly pendingOrderReminderService = inject(PendingOrderReminderService);
  private readonly vendorOrdersService = inject(VendorOrdersService);
  private readonly vendorSideMenu = inject(VendorSideMenuService);
  private readonly vendorEarningsGate = inject(VendorEarningsGateService);

  /** New-order count for sidebar badge (lightweight tab-counts API, not full tab list). */
  readonly pendingOrderCount = toSignal(
    this.vendorOrdersService.vendorTabCounts$.pipe(map((c) => c.new)),
    { initialValue: 0 },
  );

  readonly navSections = NAV_SECTIONS;
  /** lg breakpoint — sidebar pinned open at/above this width by default. */
  private static readonly DESKTOP_MIN_WIDTH = 1024;
  readonly isDesktopLayout = signal(
    typeof window !== 'undefined' && window.innerWidth >= VendorShellComponent.DESKTOP_MIN_WIDTH,
  );
  readonly isSidebarPinned = signal(
    typeof window !== 'undefined' && window.innerWidth >= VendorShellComponent.DESKTOP_MIN_WIDTH,
  );
  readonly isMobileDrawerOpen = signal(false);
  readonly isSidebarOpen = computed(() =>
    this.isDesktopLayout() ? this.isSidebarPinned() : this.isMobileDrawerOpen(),
  );
  readonly showSidebarMenuButton = computed(() => !this.isSidebarOpen());
  readonly headerSearchQuery = signal('');
  private headerSearchNavigateTimeout: ReturnType<typeof setTimeout> | null = null;
  private ignoreNextSync = false;
  readonly showOrderSearch = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map(() => this.isOrderSearchRoute(this.router.url)),
    ),
    { initialValue: this.isOrderSearchRoute(this.router.url) },
  );
  readonly isDark = this.themeService.isDark;
  readonly profile = this.authService.profile;
  readonly vendor = this.vendorContext.vendor;

  // Filter navigation: inventory for grocery only; menu categories for non-grocery only
  readonly filteredNavSections = computed(() => {
    const vendor = this.vendor();
    const isGrocery = isGroceryVendor(vendor);

    return NAV_SECTIONS.map(section => ({
      ...section,
      items: section.items.filter(item => {
        if (item.route === '/tabs/inventory' && !isGrocery) {
          return false;
        }
        if (item.route === '/tabs/custom-menu-categories' && isGrocery) {
          return false;
        }
        return true;
      })
    })).filter(section => section.items.length > 0);
  });

  constructor() {
    effect(() => {
      this.vendorSideMenu.setOpen(this.isMobileDrawerOpen());
      this.vendorSideMenu.setShowMenuButton(this.showSidebarMenuButton());
    });

    this.vendorSideMenu
      .onToggleMenu()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.toggleSidebar());

    console.log('[Vendor Shell] Constructor called');
    console.log('[Vendor Shell] Window width:', window.innerWidth);
    this.handleResize();
    
    // Log initial state
    console.log('[Vendor Shell] Initial state:', {
      isSidebarPinned: this.isSidebarPinned(),
      isMobileDrawerOpen: this.isMobileDrawerOpen(),
      profile: this.profile(),
      vendor: this.vendor(),
      navSections: this.navSections.length
    });
    
    // Subscribe to router events
    this.router.events.subscribe((event) => {
      console.log('[Vendor Shell] Router event:', event.constructor.name);
    });
  }

  ngOnDestroy(): void {
    this.vendorSideMenu.setOpen(false);
  }

  ngOnInit(): void {
    // Start listening for new orders (WebSocket + FCM foreground) and play alert sound
    this.newOrderAlertService.start();
    this.pendingOrderReminderService.start();
    // Sync header search from URL so clear button and input match after refresh
    this.syncHeaderSearchFromRoute();
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((e) => {
      this.syncHeaderSearchFromRoute();
      if (e.urlAfterRedirects.includes('/tabs/earnings')) {
        this.vendorEarningsGate.requestPasswordGate();
      }
    });
    if (this.router.url.includes('/tabs/earnings')) {
      this.vendorEarningsGate.requestPasswordGate();
    }
  }

  onSidebarNavClick(item: NavItem): void {
    if (item.route === '/tabs/earnings') {
      this.vendorEarningsGate.requestPasswordGate();
    }
  }

  readonly userInitials = computed(() => {
    const vendor = this.vendor();
    if (vendor?.name) {
      return vendor.name
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
    }
    return 'VS';
  });

  readonly userEmail = computed(() => {
    return this.profile()?.email || '';
  });

  @HostListener('window:resize')
  handleResize(): void {
    const isDesktop = window.innerWidth >= VendorShellComponent.DESKTOP_MIN_WIDTH;
    this.isDesktopLayout.set(isDesktop);
    if (isDesktop) {
      this.isSidebarPinned.set(true);
      this.isMobileDrawerOpen.set(false);
    }
  }

  toggleSidebar(): void {
    if (this.isDesktopLayout()) {
      this.isSidebarPinned.update((value) => !value);
      return;
    }
    this.isMobileDrawerOpen.update((open) => !open);
  }

  closeMobileSidebar(): void {
    this.isMobileDrawerOpen.set(false);
  }

  toggleTheme(): void {
    this.themeService.toggleDarkMode();
  }

  private isOrderSearchRoute(url: string): boolean {
    return url.includes('/tabs/orders-history') || /\/tabs\/orders(?:\?|$|\/)/.test(url);
  }

  private orderSearchBaseRoute(): '/tabs/orders' | '/tabs/orders-history' {
    return this.router.url.includes('/tabs/orders-history') ? '/tabs/orders-history' : '/tabs/orders';
  }

  private navigateOrderSearch(term: string): void {
    this.router.navigate([this.orderSearchBaseRoute()], {
      queryParams: { orderId: term || null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  onHeaderSearchInput(value: string): void {
    this.headerSearchQuery.set(value);
    if (this.headerSearchNavigateTimeout) {
      clearTimeout(this.headerSearchNavigateTimeout);
    }
    this.headerSearchNavigateTimeout = setTimeout(() => {
      this.headerSearchNavigateTimeout = null;
      this.navigateOrderSearch(this.headerSearchQuery().trim());
    }, 300);
  }

  onHeaderSearchSubmit(term: string): void {
    if (this.headerSearchNavigateTimeout) {
      clearTimeout(this.headerSearchNavigateTimeout);
      this.headerSearchNavigateTimeout = null;
    }
    this.headerSearchQuery.set(term);
    this.navigateOrderSearch(term);
  }

  clearHeaderSearch(): void {
    if (this.headerSearchNavigateTimeout) {
      clearTimeout(this.headerSearchNavigateTimeout);
      this.headerSearchNavigateTimeout = null;
    }
    this.ignoreNextSync = true;
    this.headerSearchQuery.set('');
    this.navigateOrderSearch('');
  }

  private syncHeaderSearchFromRoute(): void {
    if (this.ignoreNextSync) {
      this.ignoreNextSync = false;
      return;
    }
    const url = this.router.url;
    if (!this.isOrderSearchRoute(url)) {
      this.headerSearchQuery.set('');
      return;
    }
    const tree = this.router.parseUrl(url);
    const orderId = tree.queryParams['orderId'];
    const term = orderId != null && typeof orderId === 'string' ? orderId.trim() : '';
    this.headerSearchQuery.set(term);
  }

  async signOut(): Promise<void> {
    // Show confirmation dialog
    const confirmed = await this.dialogService.confirm({
      title: 'Sign Out',
      message: 'Are you sure you want to sign out?',
      confirmText: 'Sign Out',
      cancelText: 'Cancel',
    });

    if (!confirmed) {
      this.logger.debug('Sign out cancelled by user');
      return;
    }

    this.logger.debug('Sign out initiated from sidebar');
    try {
      await this.sessionTeardown.teardown('shell-logout');
      await this.authService.signOut();
      this.logger.debug('Sign out successful, navigating to login');
      
      // Use window.location as fallback if router navigation fails
      try {
        await this.router.navigate(['/auth/login'], { replaceUrl: true });
        this.logger.debug('Navigation complete');
      } catch (navError) {
        this.logger.warn('Router navigation failed, using window.location', navError);
        window.location.href = '/auth/login';
      }
    } catch (error: unknown) {
      // Error will be handled by global error handler
      // Fallback to window.location even on error
      window.location.href = '/auth/login';
    }
  }

  trackSection(_index: number, section: NavSection): string {
    return section.title;
  }

  trackNavItem(_index: number, item: NavItem): string {
    return item.route;
  }
}

